// src/data/blogData.js
export const blogPosts = [
{
  id: "itra-pi-aid-station-strategy",
  title: "ITRA PI（走力指数）とエイド計画で完走率を上げる！関門アウトを防ぐペース戦略",
  date: "2026-10-25",
  image: "/images/blog/itra-aid-strategy.jpg",
  category: "トレランの学び",
  summary: "自分のITRA PI（走力指数）から現実的な区間タイムを把握し、エイドでの休憩時間や関門時刻を正しく計算して完走率を高める具体的なレース戦略を解説。",
  content: `
    <p>ウルトラトレイルや長距離のトレイルランニングレースで、多くのランナーを悩ませるのが「関門時間」と「エイドステーションでの滞在時間」です。</p>

    <p>「順調に走っていたつもりなのに、後半のエイドで関門ギリギリになって焦ってしまった」「エイドで思ったより長く休んでしまい、計画が崩れてしまった」という経験を持つ方も多いのではないでしょうか。</p>

    <p>レースを安全かつ確実に完走するためには、自分の走力指標である <strong>ITRA PI（Performance Index）</strong> を把握した上で、<strong>エイドごとの到着予想と休憩時間をあらかじめ計算しておくこと</strong> が欠かせません。本記事では、ITRA PI とエイド情報を組み合わせた実践的な完走戦略について解説します。</p>

    <h2>なぜ「ITRA PI」と「エイド計画」の組み合わせが重要なのか？</h2>
    <p>単に「目標フィニッシュタイム」だけを決めて走ると、途中のコース変化（登りの厳しさや後半の疲労）に対応できず、どこかの関門で引っかかってしまうリスクが高まります。</p>

    <ul>
      <li><strong>ITRA PIを活用する理由：</strong> 自分の客観的な走力（上り・下りの適応力）に基づいて、区間ごとの現実的なペースを算出できるため。</li>
      <li><strong>エイド計画を立てる理由：</strong> 各エイドでの「補給・ドロップバッグ利用・休憩」にかかる所要時間を織り込んだ上で、次の関門までの「正味の走行可能時間」を把握するため。</li>
    </ul>

    <h2>関門アウトを防ぐための3つのステップ</h2>

    <h3>ステップ1：自身の ITRA PI とレースの「Finisher Level（FL）」を把握する</h3>
    <p>ITRA（国際トレイルランニング協会）の公式サイトや過去の完走実績から自分の PI を確認します。PI はロードのタイムとは異なり、トレイル独特の登坂力や過酷な環境への対応力を数値化したものです。</p>

    <p>一般的に、ITRA PI の数値によるレベル感の目安は以下のようになっています。</p>

    <ul>
      <li><strong>826以上：</strong> 国際大会入賞レベル</li>
      <li><strong>726〜825：</strong> 国内大会入賞レベル</li>
      <li><strong>551〜725：</strong> 上級者</li>
      <li><strong>301〜550：</strong> 中級者</li>
      <li><strong>300以下：</strong> 初級者・登山レベル</li>
    </ul>

    <p>自分の PI を把握したら、次に挑戦したいレースの <strong>Finisher Level（FL）</strong> と比較してみましょう。</p>

    <div style="background-color: #f7fafc; border-left: 4px solid #3182ce; padding: 12px 16px; margin: 16px 0; border-radius: 4px;">
      <strong style="color: #2b6cb0;">💡 Finisher Level（FL）とは？</strong><br />
      ITRA パフォーマンスインデックス（PI）を用いて、<strong>「制限時間ぎりぎりで完走するために最低限必要な走力」</strong>を推定した値です。<br />
      ・自分の PI と比較することで、そのレースを完走できる走力があるかの目安になります。<br />
      ・レースの“完走難易度”を客観的に把握できます。
    </div>

    <p>自分の PI が目標レースの FL を上回っていれば、制限時間内に完走できる十分な走力があると判断できます。逆に FL に届いていない場合は、より丁寧なペース管理やエイドでのロスタイム短縮が鍵になります。</p>

    <h3>ステップ2：各エイドでの「必要休憩時間」をリアルに見積もる</h3>
    <p>エイドは「ただ通過する場所」ではありません。特に50km以上のレースでは、以下のような滞在時間が発生します。</p>

    <ul>
      <li>水・補給食の補充（2〜3分）</li>
      <li>着替え・シューズ履き替え・ドロップバッグ整理（10〜15分）</li>
      <li>消化・ストレッチ・仮眠（15〜30分）</li>
    </ul>

    <p>これらを考慮せず「休憩0分」で計画を立ててしまうと、実際にはエイドごとに数分〜十数分の「計画とのズレ」が積み重なり、後半に大きな関門リスクとなって跳ね返ってきます。</p>

    <h3>ステップ3：区間傾斜と関門時刻を紐付けて可視化する</h3>
    <p>GPXデータの標高グラフとエイドの関門時刻を組み合わせ、「エイドを出発すべき限界の時刻」と「自分の到着予測時刻」の差（マージン）を計算します。</p>

    <h2>TrailRunSim で「走力 × エイド計画」を自動計算</h2>
    <p>TrailRunSim では、コースの GPX データとご自身の ITRA PI を入力するだけで、傾斜補正された区間ペースとエイド到着予想時刻を自動算出します。</p>

    <p>さらに、画面上の「エイド情報」入力欄で、<strong>各エイドでの休憩時間（分）を設定</strong> することで、休憩を含めたリアルな完走スケジュールをグラフと表で確認できます。</p>

    <ul>
      <li>プリセットレースを選択すれば、主要エイドの距離や関門時刻、<strong>各コースの Finisher Level（FL）</strong>が自動セット</li>
      <li>各エイドで「15分休憩」と入力すると、以降の到着時刻・関門余白がリアルタイムに再計算</li>
      <li>関門オーバーの危険がある区間が一目で判断可能</li>
    </ul>

    <p><a href="/" target="_blank" rel="noopener" style="color:#007aff;font-weight:600;">TrailRunSim でエイド計画・関門シミュレーションを試す</a></p>

    <p>トレイルランニングは、走力だけでなく「タイムマネジメント」が試されるスポーツです。</p>

    <p>自分の ITRA PI とレースの FL（Finisher Level）を見比べ、エイドでの休憩を含めた完走計画表を作成しておくことで、レース当日は焦ることなく自分の走りに集中できるようになります。次のレースに向けて、ぜひシミュレーターであなただけの完走戦略を組み立ててみてください。</p>
  `
  },
  {
  id: "trailrun-pace-calculation-guide",
  title: "トレランのペース計算はどう決める？GPXと傾斜から現実的な完走ペースを作る方法",
  date: "2026-07-03",
  image: "/images/blog/trailrun-pace-calculation.jpg",
  category: "トレランの学び",
  summary: "トレイルランニングのペースはロードのように「キロ◯分」で決められない。GPXの傾斜、累積標高、走力（ITRA PI）をもとに、現実的な完走ペースをどう作るかをまとめた。",
  content: `
    <p>トレイルランニングを始めた頃、まだ GPX データを扱う知識もなく、レース運営が公開している地図とエイド情報だけを頼りに、エクセルで手作業でペース計算をしていました。</p>

    <p>地図の標高を一つずつ拾い、区間ごとの上り・下りを自分で書き出し、「この傾斜なら何分くらいかかるだろう」と予想しながら計画を作っていました。しかし、実際に走ると想定と大きくズレることが多く、トレランのペース計算の難しさを痛感しました。</p>

    <p>その経験から、GPX の傾斜や累積標高、走力（ITRA PI）を使って、もっと現実的な完走ペースを自動で作れないかと考えるようになりました。本記事では、トレランのペース計算を「地図と手計算の時代」から「GPXと傾斜を使った精密計算」へアップデートする方法をまとめます。</p>

    <h2>トレランのペースは「キロ◯分」では決められない</h2>
    <p>ロードランニングと違い、トレイルではペースが区間ごとに大きく変わります。理由は以下の通りです。</p>
    <ul>
      <li>傾斜（上り・下り）</li>
      <li>累積標高</li>
      <li>距離による疲労</li>
      <li>路面状況（岩場・階段・砂利）</li>
      <li>走力（ITRA PI）</li>
    </ul>

    <h2>まず決めるべきこと：基準ペースと走力（ITRA PI）</h2>
    <p>最初に必要なのは、自分の基準ペース（ジョグペース）です。ロードで無理なく走れるペースが基準になります。</p>
    <p>例：ジョグ 6'00/km、ゆっくり 6'30〜7'00/km</p>

    <p>次に、ITRA Performance Index（走力指数）を把握します。PI が高いほど、スピードと過酷なコースへの適応力が高いことを示します。</p>
    <p>ITRA の公式ページでは、各レースの完走者がどれくらいの PI を持っているかを確認できます。自分の PI と照らし合わせることで、「この大会は自分の走力で完走できそうか」「どのくらいのタイム帯に入れそうか」を判断する材料にもなります。</p>

    <h2>傾斜別の目安ペース（初心者向け）</h2>
    <ul>
      <li>平坦：6'30〜7'30/km</li>
      <li>ゆるい上り（5〜10%）：10'00〜15'00/km</li>
      <li>急登（15〜25%）：18'00〜30'00/km</li>
      <li>ゆるい下り：6'00〜7'00/km</li>
      <li>急な下り：7'00〜10'00/km</li>
    </ul>

    <h2>区間ごとにペースを決めるのが正解</h2>
    <p>トレランはロードと違い、区間ごとにペースが大きく変わるスポーツです。コース全体を「ひとつのペース」で走るのは現実的ではありません。</p>

    <p>そのため、<strong>GPX の傾斜・標高・距離をもとに、区間ごとにペースを決めることが完走への近道</strong>です。</p>

    <h2>TrailRunSim を使ったペース計算（GPX × 走力）</h2>
    <p>TrailRunSim では、GPX と走力から以下を自動計算します。</p>
    <ul>
      <li>区間ごとの傾斜</li>
      <li>上り・下りのペース補正</li>
      <li>距離による疲労</li>
      <li>エイド到着予測</li>
      <li>関門判定</li>
    </ul>

    <p>これにより、手計算では難しかった「現実的な完走ペース」を自動で作ることができます。</p>

    <p><a href="/" target="_blank" rel="noopener" style="color:#007aff;font-weight:600;">TrailRunSim でペース計算する</a></p>

    <h2>ペース計算でよくある失敗</h2>
    <ul>
      <li>ロードの感覚でペースを決める</li>
      <li>累積標高を軽く見積もる</li>
      <li>下りで稼げると思いすぎる</li>
      <li>GPX を見ずに計画する</li>
    </ul>

    <h2>まとめ：トレランのペースは「区間ごと」に決める</h2>
    <p>トレランのペースは「キロ◯分」では決められません。傾斜・累積標高・走力をもとに、区間ごとにペースを決めることが大切です。</p>

    <p>TrailRunSim を使えば、GPX と走力から現実的なペース配分を自動で作ることができます。完走を目指すランナーにとって、大きな安心材料になるはずです。</p>

  `
},
{
  id: "izu-trail-journey-2014-training",
  title: "IZU TRAIL Journey 2014に向けた試走：仁科峠からゴールまで30km",
  date: "2013-11-23",
  image: "/images/blog/izu-trail-journey-2014.jpg",
  category: "トレランの学び",
  summary: "2014年3月のIZU TRAIL Journey 70kmに向けて、仁科峠からゴールまでの30kmを試走。快晴の絶景に励まされつつも、完走ペースの厳しさを実感した。",
  content: `
    <p>2014年3月10日開催予定の <strong>IZU TRAIL Journey 2014</strong> にエントリーしました。</p>
    <p>これまでの「倍々計画」に従い、次は70kmのレースに挑戦です。UTMF出場に必要なポイントも稼ぎたいという思いもありました。</p>
    <p>完走を目指すにあたり、2013年11月に試走を実施。全コースは無理なので、40km地点にある仁科峠からスタートし、ゴールまでの30kmを走ってみました。</p>
    <p>当日は快晴。事前に調べてある程度わかっていたものの、実際に走ってみると景色が素晴らしく、気持ちよく走ることができました。</p>
    <p>一方で、今日のペースを基準にすると本番での完走はギリギリかもしれない、という現実も見えてきました。70kmという距離の厳しさを改めて実感し、これからの練習課題が明確になった一日でした。</p>
    <p>※2014年の大会は、台風の影響で実施が不可能と判断され、中止になりました。</p>
  `
},
{
  id: "fujisan-trailrun-2013",
  title: "UTMFに少し近づけた気がした日。富士山麓トレイルラン18km",
  date: "2013-10-05",
  image: "/images/blog/fujisan-trailrun-2013.jpg",
  category: "レース体験",
  summary: "第7回富士山麓トレイルラン18kmロングの部に参加。UTMFの一部コースを走る大会。大雨で道はどろどろ、防雨・防寒の重要性を痛感。結果はともかく、憧れの舞台に一歩近づけた気がした。",
  content: `
    <p>2013年10月5日、第7回富士山麓トレイルラン18kmロングの部に参加。</p>
    <p>この大会は、今でも続いている人気レースで、UTMF（ウルトラトレイル・マウントフジ。今は、FUJI100）の一部コースを走ることができる貴重な機会。憧れの舞台に少しでも触れたくて、迷わずエントリーした。</p>
    <p>当日はあいにくの大雨。スタート前から道はどろどろで、シューズもウェアもすぐに重くなる。気温も下がり、雨の中を走るのは初めてだったので、防雨・防寒の重要性を身をもって学ぶことになった。</p>
    <p>この大会では、雨の中を走るのが初めてだったこともあり、装備面でも多くの学びがあった。</p>
    <p>スタート前、出店していたお店の人から「今日みたいな日はラグが深いシューズがいいよ」と教えてもらい、グリップ力の重要性を実感。ぬかるんだトレイルでは、靴底の突起（ラグ）がしっかりしているシューズが安心感につながる。</p>
    <p>また、定番の <strong>ファイントラックのドライレイヤー</strong> を勧められたのも印象的だった。汗冷えを防ぎ、雨でも体温を保ちやすいとのことで、今では自分のレース装備の定番になっている。</p>
    <p>コースは富士山麓らしい広大な森と、樹海、アップダウンのあるトレイル。ぬかるみに足を取られながらも、UTMFの選手たちが走る道を自分も踏みしめていると思うと、自然と気持ちが前向きになる。</p>
    <p>結果はどうあれ、この日走った18kmは、自分にとって「UTMFに少し近づけた気がした」大切な一歩だった。</p>
  `
},
{
  id: "nishitanzawa-adventure-2013",
  title: "川を渡り、山を越えて。SALOMON西丹沢アドベンチャーラン42km",
  date: "2013-06-08",
  image: "/images/blog/nishitanzawa.jpg",
  category: "レース体験",
  summary: "青梅高水の23kmから倍々計画で挑んだ42kmレース。川の中を渡るアドベンチャー要素と、トレイルの厳しさ。足が攣っても、暑さに負けそうでも、シミュレーションを信じて完走した7時間50分。",
  content: `
    <p>2013年6月8日。SALOMON西丹沢アドベンチャーラン42kmに出場。</p>
    <p>前回の青梅高水が23kmだったので、倍々計画の次のステップとして42kmのこの大会にエントリー。今はもう開催されていない幻の大会だけど、川の中を横断するアドベンチャー要素と、しっかりしたトレイルが組み合わさった、唯一無二のレースだった。</p>
    <p>事前にエクセルでペース配分をシミュレーション。関門は9時間。自分の走力とコースプロフィールをもとに、念入りに計画を立てた。</p>
    <p>スタートから15kmくらいまでは、川の中をジャブジャブ進む区間もありつつ、基本は林道とロード。周囲のペースが速く、ついつい引っ張られてしまう。</p>
    <p>15kmを過ぎて本格的なトレイルに入ると、最初の登りで脚が攣る。前回の青梅でも同じような症状が出たが、原因が塩分不足だとはまだ気づいていなかった。</p>
    <p>20km過ぎのエイドでは、リタイヤする人もちらほら。自分も暑さと脚の不調で少し気持ちが傾いたが、「いけるところまで行こう」と気持ちを切り替えた。</p>
    <p>30kmを過ぎると、同じペースのランナーと会話をしながら進む。エクセルでシミュレーションした通過予測と照らし合わせながら、「あと何時間でゴールできるか」を計算しつつ、冷静にペースを維持。</p>
    <p>結果、7時間50分で完走。関門の9時間には余裕を持ってゴールできた。ロードのフルマラソンの完走も目標にしていたが、トレイルでこの距離を走りきれたのは自信につながった。</p>
    <p>川を渡り、山を越え、暑さと脚の攣りに耐えながらの42km。今でも「また開催してほしい」と思う、思い出深いレースだった。</p>
  `
},
{
  id: "utmf-spectator-2013",
  title: "観るだけじゃ足りなくなった日。2013年UTMFで感じたこと",
  date: "2025-08-01",
  image: "/images/blog/utmf-spectator-2013.jpg",
  category: "トレランの学び",
  summary: "1年前に初めて観戦したUTMF。その翌年、再び現地へ。今度は“走る側”の視点を少し持って訪れたからこそ、見えるものが変わっていた。そして、もう一度強く思った。「自分もいつかこの舞台に立ちたい」と。",
  content: `
    <p>2013年4月27日。再びウルトラトレイル・マウントフジ（UTMF）の観戦へ。</p>
    <p>ちょうど1年前、初開催だった第1回大会を観に行き、「いつかこんな舞台を走ってみたい」と思ったのがすべての始まりだった。そしてこの1年間で、トレイルを少しずつ走れるようになり、レースにも挑戦してきた。</p>
    <p>再び訪れた富士山麓の空気は、あの頃とは違って感じられた。「ただすごいな」と思っていたランナーたちの動きに、少しだけ自分の経験を重ねられるようになった気がした。</p>
    <p>日をまたいで走り続けるって、どんな感覚なんだろう。夜のトレイル、身体の限界、メンタル。そのすべてを受け入れながら、100マイル先のゴールに向かう。そんな世界があることが、ただただすごいと思った。</p>
    <p>どんな練習を積めば、ここに辿り着けるんだろう。距離だけじゃない。装備も補給も、戦略もメンタルも――積み重ねるべきものの多さに、あらためて圧倒される。</p>
    <p>ゴール地点では、走り終えたランナーの表情がどれも素敵だった。喜びと達成感だけじゃなく、何か深いところから湧き出たような「やり切った」表情。それを見て、ますます気持ちが高まった。</p>
    <p>この一年、自分にとっては8kmや30kmがやっとだった。「俺にできるのか？」という反面、でも「やっぱり、自分もここを走ってみたい」と、改めて強く思った。</p>
    <p>観戦は、ただ眺めるだけじゃなかった。走ることに向き合うきっかけを、またひとつくれた時間だった。</p>
  `
},
{
  id: "ome-takomizu-2013",
  title: "青梅高水トレイル：10か月目の挑戦",
  date: "2013-04-07", 
  category: "レース体験",
  summary: "初レースから10か月。距離が3倍の青梅高水トレイル30kmに挑戦。実際のログは23kmだったけれど、心と脚にはしっかり“30kmの壁”があった。",
  content: `
    <p>トレランを始めて10か月目。初レースの8kmから一気に飛躍して、青梅高水トレイル30kmに挑戦することになった。</p>
    <p>公表では30kmだけど、ガーミンのログでは23km。とはいえ、自分にとっては十分すぎるチャレンジ距離だった。</p>
    <p>青梅駅を降りて歩き出すと、周囲にはランナーだらけ。みんな速そうに見えて、すでにプレッシャー。会場まで数キロ歩くだけでちょっと疲れていた。</p>
    <p>今回は“完走”を目標に。ただ、8km先の関門でひっかかる人が多いと聞き、とりあえず全開で飛ばす…という戦略ミス。その後の展開は言わずもがな。</p>
    <p>なんとか5分前に関門突破。折り返し地点、高水山常福院のエイドに到着して補給。これがやりたかった。トレランの“エイド文化”にちょっと感動。</p>
    <p>まわりに人がいたので安心していたが、折り返してすぐの登りロードで脚が攣る。今ならわかる塩分不足。でも当時はそんな知識ゼロだった。</p>
    <p>残り5km地点、周囲もまばら。関門時間もよく把握しておらず「あと30分しかない」と勘違いし、ほぼ諦めモードに。</p>
    <p>すると、後ろから来たランナーが「もうちょっとがんばりましょー」と声をかけてくれた。その方に「あと30分だと間に合わないかもですね」と言ったら、「え？あと1時間ありますよ」と。</p>
    <p>衝撃。そして、力が戻ってくる。そこから残りの気力を振り絞り、10分前になんとかゴールできた。</p>
    <p>走った距離は23km。でも、自分にとっては“30kmの壁”を越えた実感があるレースだった。</p>
  `
},
{
  id: "buying-first-gear",
  title: "装備デビューは原宿でこっそり",
  date: "2012-08-11",
  image: "/images/blog/buying-first-gear.jpg",
  category: "装備や準備", 
  summary: "誰にも言わずに始めたトレラン。最初のザックとハイドレを買った日の話。",
  content: `
    <p>トレランを始めようと思ったものの、まず何から揃えればいいかもわからず、とにかく調べまくってました。ある程度の知識がついてきた頃、意を決して原宿のアートスポーツへ。</p>
    <p>誰にも「始めた」とは言ってなかった。三日坊主だったら恥ずかしいし、続けられるか自信もなかったので。だから、装備を買いに行ったのもひとり、こっそりでした。</p>
    <p>この日はザックとハイドレーション狙い。シューズはなんとなくわかってたし、まずは背負うものからかなと。当時は、今みたいなフラスクじゃなくて、チューブ付きの大きなハイドレが主流でした。</p>
    <p>店員さんに「どんな大会に出るんですか？」と聞かれたときは、ちょっとドキッとして、「短いやつです、たぶん…」と曖昧に返答。でも、ついでに「UTMFっていうの、いつか出てみたいんですよね」とポロっと言ってみたら、「UTMFもいいけど、自分はハセツネ系かなー」と返ってきました。</p>
    <p>そのときは「ハセツネ系？なにそれ？」状態。でも今なら、あのとき店員さんが言ってた意味、よくわかります。</p>
    <p>袋の中には、新品のザックとハイドレ。まだ汗も泥も知らないピカピカの装備。でもあの日、こっそり自分の中で「よし、ちょっと本気出してみるか」ってスイッチが入った気がします。</p>
  `
},
{
  id: "first-marathon-practice-run",
  title: "走り始めて3か月。フルマラソンに挑戦してみた",
  date: "2012-10-08",
  category: "トレランの学び", 
  summary: "走り始めて3か月。自分の体がどこまで耐えられるか試したくて、地図で43kmのコースを組んでフルマラソンに挑戦。",
  content: `
    <p>まだ走り始めて3か月くらいのころ。</p>
    <p>大会に出る予定はなかったけど、「一度、自分の体でフルマラソンってどんなものか確かめてみたい」と思い立ち、地図を眺めながら43kmのコースを組んでみました。</p>
    <p>当日は、ちょっとした冒険気分。ザックには、おにぎりやパン、チョコ、それから着替えもちょこっと入れて、完全に“ひとり旅”スタイルです。</p>
    <p>走り始めは快調。景色を楽しみながら、知らない街を抜けたり、公園で小休憩したり。ペースはのんびりだけど、気持ちは上々でした。</p>
    <p>…が、30kmを過ぎたあたりから、脚に異変。じわじわと動かなくなっていき、35km近くではもうふらふら。足の裏が地面から離れるたびに「いたっ」と声が出そうになる感じ。</p>
    <p>それでも、ザックに忍ばせておいたおにぎりを食べたら、驚くほど回復。まさかの“米パワー”発動で、最後まで走りきることができました。</p>
    <p>完走したあと、時計を見て「ほんとに走ったんだ」としみじみ。記録はないけど、自分の中ではひとつの区切りになった大切な1日でした。</p>
  `
},
{
  id: "how-to-enjoy-race",
  title: "レースの楽しみかた",
  date: "2025-06-17",
  category: "トレランの学び", 
  summary: "順位やタイムだけじゃない。トレイルレースには、それぞれの楽しみ方がある。",
  content: `
    <p>レースと聞くと、「速く走らなきゃいけない」「他のひとに負けたくない」といったプレッシャーを感じる人も多いかもしれません。でも、実はレースには「競争」以外のたくさんの楽しみ方があります。</p>
    <p>僕にとってレースは「時間制限のある超安全な登山」みたいなイメージです。普段ひとりでは行けないような山域や行ったことが無い山道を、安全に、しかも補給しながら走れる。それだけで非日常の冒険です。</p>
    <p>また、参加者とのふれあいも醍醐味のひとつ。見知らぬ人同士が、同じ目標に向かって苦楽を共にする。追い抜いたり抜かれたりしながら、いつの間にか会話が始まり（だいたい、持ってるギアの会話が多い）、気が付けば苦しいはずの登りもいつの間にか終わっていたり。こうした「出会い」もレースならではの魅力です。</p>
    <p>順位やタイムを目指すのもいいけど、関門時間に間に合うよう、ある程度プレッシャーを感じながら、「山を味わい尽くす」「仲間と楽しむ」「記録より記憶」といったマインドで臨むと、プレッシャーも減って、景色も楽しめるようになります。</p>
    <p>もしこれからレースに出る方がいたら、ぜひ「自分なりの楽しみ方」を見つけてほしいなと思います。</p>
  `
},
{
  id: "challenge-to-ome-takomizu",
  title: "100マイルへの一歩：青梅高水30kmへの挑戦",
  date: "2012-11-18",
  category: "トレランの学び", 
  summary: "100マイルを目指し、距離を段階的に伸ばす計画。次の目標は、半年後の青梅高水トレイルラン30km。",
  content: `
    <p>「いつか100マイルを走りたい」</p>
    <p>そんな漠然とした思いが、当時頭から離れませんでした。これまでの自分には考えられなかった、壮大な目標です。どこから手をつけていいかもわからないけれど、とにかく一歩を踏み出さなければ何も始まりません。</p>
    <p>そこで考えたのが、段階的に距離を伸ばしていくというアプローチです。まずは経験したことのある8kmからスタートし、次はその倍の16km、そしてさらに倍の32kmというように、少しずつステップアップしていこうと決めました。</p>
    <p>16kmは、これまでもロードで走った経験があるので、トレイルであっても、なんとなくいけそうな気がしていました。問題は次の32km。これは未知の領域です。</p>
    <p>さて、どのレースに挑戦しようかと調べていたとき、目に留まったのが「青梅高水トレイルラン」でした。例年4月上旬に開催されており、ちょうど半年後2013年4月という絶妙なタイミング。そして、何より30km部門があるのが決め手となりました。思い立ったら吉日、スポーツエントリーでエントリー完了！！</p>
    <p>もちろん、いきなり32kmを走り切れる保証はありませんでしたが、この目標があることで、日々のトレーニングにもより一層熱が入りました。</p>
  `
},
{
  id: "first-trail-race",
  title: "第1回レースの記録：宇都宮トレイルラン",
  date: "2012-10-28",
  category: "レース体験", 
  summary: "初めてのトレイルレース、宇都宮トレイルラン。目標は完走。そして、少しの順位への欲。",
  content: `
    <p>初めて出場したレースは「宇都宮トレイルラン」でした。ショート（8km）とミドル（16km）の部があり、私はショートにエントリーしました。</p>
    <p>本番前に行われた試走会にも参加してみました。集まったランナーたちは皆、装備も脚も本格的で、正直圧倒されました。「本当に自分がここを走れるのだろうか」と不安になったのを覚えています。</p>
    <p>レース当日までの間、自分なりに準備を重ねました。どれくらいのペースで走れば完走できるのか、Excelを使ってシミュレーションしました。このシミュレータの原型です。</p>
    <p>当日の目標は「まずは完走」。それが第一でした。でも、心のどこかで、「できれば順位も狙ってみたい」という気持ちもあったのです。</p>
    <p>レースは予想通りきつく、ペース配分や給水などまったくわかっていなかったため、登りでは何度も脚が止まり、挙句の果ては内ももが攣るという拷問。それでも、沿道の声援や、一緒に走るランナーに励まされ、何とかフィニッシュラインを踏むことができました。</p>
    <p>順位？ 想像していたよりも後ろでしたが、久しぶりに「やりきった」感満載の一日でした。</p>
  `
},
{
  id: "first-trail-run",
  title: "初めて山を走った日のこと",
  date: "2012-09-24",
  image: "/images/blog/first-trail-run.jpg",
  category: "トレランの学び", 
  summary: "トレイルを初めて走ったあの日。登りの厳しさと、少しの達成感。",
  content: `
    <p>初めて山を走ったのは、2012年の秋でした。今では、レースも行われている皆野にある美の山、標高でいえば600メートルほどの小さな山です。</p>
    <p>初めて買ったトレランシューズ（TNFのSingle-Trackだったかな）に、ハイドレーション。今思えば格好は一人前で、「とにかくやってみよう」という気持ちだけでスタートしました。</p>
    <p>山登り自体あまり経験がないため、登りはすぐに息が上がり、走るどころか歩くのも辛い状態。足元に伸びる木の根に戸惑いながら、とにかく走らないといけないと思い、必死に進んでいきました。</p>
    <p>途中、木々の間から差し込む光がとても印象的で、足元に広がる影と光の模様がまるで自然が描いたアートのようでした。静かな森の中で、自分の呼吸と足音だけが響いていて、「ああ、こういう時間を味わいたかったんだ」と思えた瞬間でした。</p>
    <p>やっとのことでピークにたどり着いたとき、目に入ったのは、街を見下ろす小さな展望台と、思ったよりも回復力がある自分。心の中に、うっすらと「トレラン、いけるかな」と思える何かが残っていました。</p>
    <p>この日を境に、山を走るという行為が、特別なものになりました。</p>
  `
},
{
  id: "why-i-started-trailrunning",
  title: "トレイルランニングを始めたきっかけ",
  date: "2012-05-19",
  image: "/images/blog/why-i-started-trailrunning.jpg",
  category: "トレランの学び", 
  summary: "トレイルランを始めたきっかけ？2012年第1回ウルトラトレイル・マウントフジ (UTMF)",
  content: `
    <p>トレイルランニングを始めたきっかけは、2012年に開催された第1回ウルトラトレイル・マウントフジ（UTMF）を観戦したことでした。</p>
    <p>昼夜を問わず100マイルという長大な距離を走り続けるランナーたちの姿に衝撃を受け、「ただただ、かっこいい」と心を奪われました。</p>
    <p>とはいえ当時の私は、1kmすらまともに走れず、山にはまったく興味がなく、しかも煙草を吸っているという、山やランニングとは無縁の生活を送っていました。</p>
    <p>まずは10kmに挑戦してみたものの、走り続けることなど到底できず、ふくらはぎはパンパン。現実は甘くありませんでした。</p>
    <p>それでも少しずつ装備を整え、低山から走り始めるようになりました。そして走り始めて約半年後、ついにレースデビュー。距離は8km、あいにくの雨でしたが、エイドもあり、応援もあり、何とも言えない充実感がありました。</p>
    <p>これまで何度も失敗してきた禁煙も、レースにエントリーした翌日から、驚くほどすっぱりとやめることができました。</p>
  `
},
{
  id: "izu-trail-journey-2014-training-en",
  title: "Training for IZU TRAIL Journey 2014: 30km from Nishi-Ito Pass to the Finish",
  date: "2013-11-23",
  image: "/images/blog/izu-trail-journey-2014.jpg",
  category: "Trail Running Notes (EN)",
  summary: "In preparation for the 70km IZU TRAIL Journey 2014, I ran a 30km training section from Nishi-Ito Pass to the finish. The clear skies and stunning views lifted my spirits, but I also realized how tough the pace would be to finish within the cutoff.",
  content: `
    <p>I signed up for the <strong>IZU TRAIL Journey 2014</strong>, scheduled for March 10, 2014.</p>
    <p>Following my “doubling plan,” the next step was a 70km race. I also wanted to collect qualifying points for UTMF, so this was an important challenge.</p>
    <p>To prepare, I went for a training run in November 2013. Running the entire course wasn’t realistic, so I started from Nishi-Ito Pass at around the 40km point and covered the final 30km to the finish.</p>
    <p>The weather was perfect—clear skies and breathtaking views. I had read about the scenery beforehand, but experiencing it in person was something else. It was uplifting and motivating.</p>
    <p>At the same time, I realized that at today’s pace, finishing the full 70km within the cutoff would be very difficult. It was a sobering reminder of the challenge ahead, and it gave me a clear sense of what I needed to work on in training.</p>
    <p><em>Note: The 2014 race was ultimately canceled due to typhoon damage, making it impossible to hold the event.</em></p>
  `
},
{
  id: "fujisan-trailrun-2013-en",
  title: "A Day I Felt Closer to UTMF: 18km Trail Run at the Foot of Mt. Fuji",
  date: "2013-10-05",
  image: "/images/blog/fujisan-trailrun-2013.jpg",
  category: "Trail Running Notes (EN)",
  summary: "I joined the 7th Mt. Fuji Foothills Trail Run, 18km long course. The race covers part of the UTMF course. Heavy rain turned the trails into mud, teaching me the importance of rain and cold protection. Regardless of the result, I felt one step closer to the stage I admire.",
  content: `
    <p>On October 5, 2013, I took part in the 7th Mt. Fuji Foothills Trail Run, 18km long course.</p>
    <p>This race is still a popular event today, and it offers a rare chance to run on part of the UTMF (Ultra-Trail Mt. Fuji, now called FUJI100) course. Wanting to touch even a small piece of that dream stage, I entered without hesitation.</p>
    <p>Unfortunately, the day brought heavy rain. Even before the start, the trails were muddy, and my shoes and clothes quickly became heavy. The temperature dropped, and since it was my first time running in the rain, I learned firsthand the importance of rain and cold protection.</p>
    <p>Because it was my first time racing in such conditions, I also gained many lessons about gear.</p>
    <p>Before the start, a shop staff member told me, “On a day like this, shoes with deep lugs are best.” I realized how crucial grip is. On muddy trails, shoes with solid lugs on the sole give a real sense of security.</p>
    <p>I was also impressed when they recommended the classic <strong>Finetrack Dry Layer</strong>. It prevents chill from sweat and helps retain body heat even in the rain. Since then, it has become a staple in my race gear.</p>
    <p>The course featured the vast forests and lava fields typical of Mt. Fuji’s foothills, with plenty of ups and downs. Even as I struggled through the mud, knowing I was running on the same trails as UTMF athletes gave me strength and motivation.</p>
    <p>No matter the result, the 18km I ran that day was a precious step—one that made me feel a little closer to UTMF.</p>
  `
},
{
  id: "utmf-spectator-2013-en",
  title: "Not Just Watching: What I Felt at UTMF 2013",
  date: "2013-04-27",
  image: "/images/blog/utmf-spectator-2013.jpg",
  category: "Trail Running Notes (EN)",
  summary: "In 2013, I went to see the Ultra-Trail Mt. Fuji (UTMF) for the second time. This time, I wasn’t just a spectator—I had already started trail running myself. Watching the runners through a new lens made me dream even stronger: one day, I want to stand on this stage.",
  content: `
    <p>On April 27, 2013, I visited the Ultra-Trail Mt. Fuji (UTMF) again as a spectator. 
    A year earlier, I had seen the very first edition of this 100-mile race, and it completely changed my life. 
    It was the spark that made me start trail running.</p>

    <p>One year later, I returned to the same place, but this time I was different. 
    I had already run some races myself, from 8km to 30km, and I could imagine—just a little—what the UTMF runners were going through.</p>

    <p>Running through the night, pushing the body to its limits, and keeping the mind strong for 100 miles… 
    Watching them, I felt both awe and curiosity: what kind of training, gear, and mindset does it take to reach this level?</p>

    <p>At the finish line, every runner’s face told a story. 
    Not just joy or relief, but a deep expression of “I gave everything.” 
    Seeing that, I felt my own motivation grow stronger.</p>

    <p>Back then, 8km or 30km was already a huge challenge for me. 
    But standing there, I thought: “Someday, I want to be here. I want to run UTMF.”</p>

    <p>Spectating wasn’t just watching. 
    It was another step in my journey as a runner, and a reminder of why I started.</p>
  `
}
];
